import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Autocomplete,
  Box,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Menu,
  MenuItem,
  TextField,
  Tooltip,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { edgeOnboardApi, locationsApi } from '../../api/services';
import type { EdgeOnboardCustomerUser, Location } from '../../api/config';
import { extractApiError } from '../../utils/apiErrors';
import { EoChip, EoEmpty, EoIcon, EoPage, EoPageHeader, formatEoDate, useEoSnackbar } from './eoShared';

// Status filter values. Active is the default view — soft-deleted users are
// hidden until an admin explicitly asks for them.
type StatusFilter = 'active' | 'deleted' | 'all';
const STATUS_LABELS: Record<StatusFilter, string> = {
  active: 'Active',
  deleted: 'Soft-deleted',
  all: 'All',
};
const STATUS_OPTIONS: StatusFilter[] = ['active', 'deleted', 'all'];

// Mirrors the engine's CustomerUser::MIN_PASSWORD_LENGTH.
const MIN_PASSWORD_LENGTH = 8;

const fullName = (u: EdgeOnboardCustomerUser) => `${u.first_name} ${u.last_name ?? ''}`.trim();

// One free-text "Full name" input; the engine keeps first/last columns.
// First word → first_name, remainder → last_name (may be empty).
function splitFullName(name: string): { first_name: string; last_name: string } {
  const parts = name.trim().split(/\s+/);
  return { first_name: parts[0] ?? '', last_name: parts.slice(1).join(' ') };
}

// Customer users are the on-site accounts that tag products during a go-live.
// Admins here create them, reset their passwords, and soft-delete/restore them.
export const OnboardingUsersPage: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { showSuccess, showError, snackbarElement } = useEoSnackbar();

  const [search, setSearch] = React.useState('');
  const [statusFilter, setStatusFilter] = React.useState<StatusFilter>('active');
  const [statusMenuAnchor, setStatusMenuAnchor] = React.useState<null | HTMLElement>(null);
  const [locationFilter, setLocationFilter] = React.useState<Location | null>(null);
  const [locationMenuAnchor, setLocationMenuAnchor] = React.useState<null | HTMLElement>(null);

  const [addOpen, setAddOpen] = React.useState(false);
  const [editUser, setEditUser] = React.useState<EdgeOnboardCustomerUser | null>(null);
  const [resetUser, setResetUser] = React.useState<EdgeOnboardCustomerUser | null>(null);
  const [deleteUser, setDeleteUser] = React.useState<EdgeOnboardCustomerUser | null>(null);

  // Always fetch everything (including soft-deleted) — the Status filter is
  // applied client-side so switching views never needs a refetch.
  const usersQuery = useQuery({
    queryKey: ['edge-onboard', 'customer-users', 'all'],
    queryFn: () => edgeOnboardApi.customerUsers.getAll({ include_deleted: true }),
  });
  const locationsQuery = useQuery({
    queryKey: ['edge-onboard', 'locations-all'],
    queryFn: () => locationsApi.getAll(),
  });

  const locations = React.useMemo(() => locationsQuery.data ?? [], [locationsQuery.data]);
  const locationsById = React.useMemo(() => {
    const map = new Map<number, Location>();
    locations.forEach(l => map.set(l.id, l));
    return map;
  }, [locations]);

  const allUsers = React.useMemo(() => usersQuery.data ?? [], [usersQuery.data]);
  const activeCount = React.useMemo(() => allUsers.filter(u => !u.deleted_at).length, [allUsers]);
  const deletedCount = React.useMemo(() => allUsers.filter(u => u.deleted_at).length, [allUsers]);

  const rows = React.useMemo(() => {
    let list = allUsers;
    if (statusFilter === 'active') list = list.filter(u => !u.deleted_at);
    else if (statusFilter === 'deleted') list = list.filter(u => u.deleted_at);
    if (locationFilter) list = list.filter(u => u.location_id === locationFilter.id);
    const term = search.trim().toLowerCase();
    if (term) {
      list = list.filter(
        u =>
          (u.email ?? '').toLowerCase().includes(term) ||
          (u.username ?? '').toLowerCase().includes(term) ||
          fullName(u).toLowerCase().includes(term),
      );
    }
    return list;
  }, [allUsers, statusFilter, locationFilter, search]);

  const invalidateUsers = React.useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: ['edge-onboard', 'customer-users'] });
  }, [queryClient]);

  const restoreMutation = useMutation({
    mutationFn: (id: number) => edgeOnboardApi.customerUsers.restore(id),
    onSuccess: () => {
      showSuccess('User restored');
      invalidateUsers();
    },
    // A 409 means an active user has since taken that username — surface the
    // server's explanation so ops knows why the restore was refused.
    onError: err => showError(extractApiError(err, 'Could not restore user')),
  });

  const isLoading = usersQuery.isLoading || locationsQuery.isLoading;
  const loadError = usersQuery.error || locationsQuery.error;

  return (
    <EoPage>
      <EoPageHeader
        title="Users"
        sub="Customer users who log in on-site to tag products during a go-live."
        action={
          <button className="ea-btn contained" onClick={() => setAddOpen(true)}>
            <EoIcon name="add" />
            Add Customer User
          </button>
        }
      />

      <div className="ea-filter-bar">
        <div className="ea-filter wide">
          <EoIcon name="search" />
          <input
            placeholder="Search email, username, or name…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            aria-label="Search users"
          />
        </div>

        <button
          className={`ea-filter${statusFilter !== 'active' ? ' has-value' : ''}`}
          onClick={e => setStatusMenuAnchor(e.currentTarget)}
          aria-haspopup="menu"
        >
          {statusFilter !== 'active' ? (
            <>
              <span className="label-prefix">Status:</span>
              {STATUS_LABELS[statusFilter]}
              <span
                className="ea-ico clear"
                role="button"
                aria-label="Clear status filter"
                onClick={e => {
                  e.stopPropagation();
                  setStatusFilter('active');
                }}
              >
                close
              </span>
            </>
          ) : (
            <>
              <EoIcon name="filter_list" />
              Status
              <EoIcon name="expand_more" />
            </>
          )}
        </button>
        <Menu
          anchorEl={statusMenuAnchor}
          open={Boolean(statusMenuAnchor)}
          onClose={() => setStatusMenuAnchor(null)}
        >
          {STATUS_OPTIONS.map(s => (
            <MenuItem
              key={s}
              selected={s === statusFilter}
              onClick={() => {
                setStatusFilter(s);
                setStatusMenuAnchor(null);
              }}
            >
              {STATUS_LABELS[s]}
            </MenuItem>
          ))}
        </Menu>

        <button
          className={`ea-filter${locationFilter ? ' has-value' : ''}`}
          onClick={e => setLocationMenuAnchor(e.currentTarget)}
          aria-haspopup="menu"
        >
          {locationFilter ? (
            <>
              <span className="label-prefix">Location:</span>
              {locationFilter.name}
              <span
                className="ea-ico clear"
                role="button"
                aria-label="Clear location filter"
                onClick={e => {
                  e.stopPropagation();
                  setLocationFilter(null);
                }}
              >
                close
              </span>
            </>
          ) : (
            <>
              <EoIcon name="business" />
              Location
              <EoIcon name="expand_more" />
            </>
          )}
        </button>
        <Menu
          anchorEl={locationMenuAnchor}
          open={Boolean(locationMenuAnchor)}
          onClose={() => setLocationMenuAnchor(null)}
        >
          {locations.map(l => (
            <MenuItem
              key={l.id}
              selected={l.id === locationFilter?.id}
              onClick={() => {
                setLocationFilter(l);
                setLocationMenuAnchor(null);
              }}
            >
              {l.name}
            </MenuItem>
          ))}
        </Menu>

        <div className="spacer" />
        <span className="results">
          <strong>{activeCount}</strong> active · {deletedCount} soft-deleted
        </span>
      </div>

      {loadError ? (
        <Alert severity="error">{extractApiError(loadError, 'Failed to load users')}</Alert>
      ) : isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <div className="ea-paper" style={{ overflow: 'hidden' }}>
          {rows.length === 0 ? (
            <EoEmpty
              icon="person"
              message={
                allUsers.length
                  ? 'No users match the current filters.'
                  : 'No customer users yet — add one so they can sign in during a go-live.'
              }
            />
          ) : (
            <div className="eo-table-wrap">
            <table className="eo-table">
              <thead>
                <tr>
                  <th>Sign-in</th>
                  <th>Full Name</th>
                  <th>Location</th>
                  <th>Created</th>
                  <th>Status</th>
                  <th>Password</th>
                  <th style={{ textAlign: 'right' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map(u => {
                  const loc = u.location_id != null ? locationsById.get(u.location_id) : undefined;
                  const isDeleted = Boolean(u.deleted_at);
                  return (
                    <tr key={u.id} className={isDeleted ? 'eo-row-muted' : undefined}>
                      <td>
                        {/* Email is the identity; username only exists on Replit-migrated accounts. */}
                        <div className="mono">{u.email ?? u.username}</div>
                        {u.email && u.username && (
                          <div className="muted" style={{ fontSize: 11, marginTop: 2 }}>
                            Replit username: <span className="mono">{u.username}</span>
                          </div>
                        )}
                      </td>
                      <td>{fullName(u)}</td>
                      <td>
                        {loc ? (
                          <>
                            {loc.name}
                            {loc.hexahq_account_id ? (
                              <span className="muted mono"> · HQ #{loc.hexahq_account_id}</span>
                            ) : null}
                          </>
                        ) : (
                          <span className="muted">—</span>
                        )}
                      </td>
                      <td className="muted">{formatEoDate(u.created_at)}</td>
                      <td>
                        {isDeleted ? (
                          <EoChip tone="default" icon="do_not_disturb_on">
                            Soft-deleted
                          </EoChip>
                        ) : (
                          <EoChip tone="success" icon="check_circle">
                            Active
                          </EoChip>
                        )}
                      </td>
                      <td>
                        {u.must_change_password ? (
                          <EoChip tone="warning" icon="key">
                            Must change
                          </EoChip>
                        ) : (
                          <span className="muted">—</span>
                        )}
                      </td>
                      <td style={{ textAlign: 'right' }}>
                        {isDeleted ? (
                          <button
                            className="ea-icon-btn"
                            title="Restore"
                            disabled={restoreMutation.isPending}
                            onClick={() => restoreMutation.mutate(u.id)}
                          >
                            <EoIcon name="restore_from_trash" />
                          </button>
                        ) : (
                          <>
                            {/* Fake door: gauging demand for automated
                                onboarding emails before building them. */}
                            <Tooltip title="Send onboarding email — coming soon" arrow>
                              <span>
                                <button className="ea-icon-btn" disabled aria-label="Send onboarding email (coming soon)">
                                  <EoIcon name="outgoing_mail" />
                                </button>
                              </span>
                            </Tooltip>
                            <button className="ea-icon-btn" title="Edit" onClick={() => setEditUser(u)}>
                              <EoIcon name="edit" />
                            </button>
                            <button
                              className="ea-icon-btn"
                              title="Reset password"
                              onClick={() => setResetUser(u)}
                            >
                              <EoIcon name="lock_reset" />
                            </button>
                            <button
                              className="ea-icon-btn"
                              title="Soft-delete"
                              onClick={() => setDeleteUser(u)}
                            >
                              <EoIcon name="delete" />
                            </button>
                          </>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            </div>
          )}
        </div>
      )}

      <AddUserDialog
        open={addOpen}
        onClose={() => setAddOpen(false)}
        locations={locations}
        onCreated={() => {
          setAddOpen(false);
          // Next step in the setup flow — jump straight to go-live creation.
          showSuccess('Customer user created', {
            label: 'Create go-live',
            onClick: () => navigate('/onboarding/go_lives?create=1'),
          });
          invalidateUsers();
        }}
      />
      <EditUserDialog
        user={editUser}
        onClose={() => setEditUser(null)}
        locations={locations}
        onSaved={() => {
          setEditUser(null);
          showSuccess('Customer user updated');
          invalidateUsers();
        }}
      />
      <ResetPasswordDialog
        user={resetUser}
        onClose={() => setResetUser(null)}
        onReset={() => {
          setResetUser(null);
          showSuccess('Password reset — sessions revoked');
          invalidateUsers();
        }}
      />
      <DeleteUserDialog
        user={deleteUser}
        onClose={() => setDeleteUser(null)}
        onDeleted={() => {
          setDeleteUser(null);
          showSuccess('Customer user soft-deleted');
          invalidateUsers();
        }}
      />
      {snackbarElement}
    </EoPage>
  );
};

// ---- Add ----

interface AddUserDialogProps {
  open: boolean;
  onClose: () => void;
  locations: Location[];
  onCreated: () => void;
}

const AddUserDialog: React.FC<AddUserDialogProps> = ({ open, onClose, locations, onCreated }) => {
  const [name, setName] = React.useState('');
  const [email, setEmail] = React.useState('');
  const [phone, setPhone] = React.useState('');
  const [location, setLocation] = React.useState<Location | null>(null);
  const [password, setPassword] = React.useState('');
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (open) {
      setName('');
      setEmail('');
      setPhone('');
      setLocation(null);
      setPassword('');
      setError(null);
    }
  }, [open]);

  const createMutation = useMutation({
    mutationFn: () =>
      edgeOnboardApi.customerUsers.create({
        password,
        ...splitFullName(name),
        email: email.trim(),
        phone: phone.trim() || null,
        location_id: location?.id ?? null,
      }),
    onSuccess: onCreated,
    onError: err => setError(extractApiError(err, 'Failed to create customer user')),
  });

  const canSubmit =
    email.trim().length > 0 && name.trim().length > 0 && password.length >= MIN_PASSWORD_LENGTH;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ className: 'eo-scope' }}>
      <DialogTitle sx={{ fontFamily: '"Plus Jakarta Sans", sans-serif', fontWeight: 600 }}>
        Add Customer User
      </DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2.25, pt: '8px !important' }}>
        {error && <Alert severity="error">{error}</Alert>}
        <TextField
          label="Full name"
          required
          value={name}
          onChange={e => setName(e.target.value)}
        />
        <TextField
          label="Email"
          type="email"
          required
          value={email}
          onChange={e => setEmail(e.target.value)}
          helperText="The customer signs in with this address."
        />
        <TextField
          label="Phone"
          value={phone}
          onChange={e => setPhone(e.target.value)}
          helperText="Optional."
        />
        <Autocomplete
          options={locations}
          value={location}
          onChange={(_, value) => setLocation(value)}
          getOptionLabel={l => l.name}
          renderInput={params => (
            <TextField {...params} label="Location" helperText="Optional — the go-live they work is assigned separately." />
          )}
        />
        <TextField
          label="Password"
          type="password"
          required
          value={password}
          onChange={e => setPassword(e.target.value)}
          helperText="At least 8 characters. The customer is asked to change it on first sign-in."
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <button className="ea-btn text" onClick={onClose} disabled={createMutation.isPending}>
          Cancel
        </button>
        <button
          className="ea-btn contained"
          disabled={!canSubmit || createMutation.isPending}
          onClick={() => {
            setError(null);
            createMutation.mutate();
          }}
        >
          {createMutation.isPending ? 'Adding…' : 'Add Customer User'}
        </button>
      </DialogActions>
    </Dialog>
  );
};

// ---- Edit ----

interface EditUserDialogProps {
  user: EdgeOnboardCustomerUser | null;
  onClose: () => void;
  locations: Location[];
  onSaved: () => void;
}

const EditUserDialog: React.FC<EditUserDialogProps> = ({ user, onClose, locations, onSaved }) => {
  const [name, setName] = React.useState('');
  const [email, setEmail] = React.useState('');
  const [phone, setPhone] = React.useState('');
  const [location, setLocation] = React.useState<Location | null>(null);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (user) {
      setName(`${user.first_name} ${user.last_name}`.trim());
      setEmail(user.email ?? '');
      setPhone(user.phone ?? '');
      setLocation((user.location_id != null && locations.find(l => l.id === user.location_id)) || null);
      setError(null);
    }
    // `locations` deliberately omitted: a background refetch must not wipe
    // in-progress edits. The find above uses whatever list is loaded when
    // the dialog opens, which is what the picker shows anyway.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const updateMutation = useMutation({
    mutationFn: () => {
      if (!user) throw new Error('No user');
      return edgeOnboardApi.customerUsers.update(user.id, {
        ...splitFullName(name),
        email: email.trim() || null,
        phone: phone.trim() || null,
        location_id: location?.id ?? null,
      });
    },
    onSuccess: onSaved,
    onError: err => setError(extractApiError(err, 'Failed to update customer user')),
  });

  // Email is required unless the account still has a legacy username.
  const canSubmit = name.trim().length > 0 && (email.trim().length > 0 || Boolean(user?.username));

  return (
    <Dialog
      open={Boolean(user)}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ className: 'eo-scope' }}
    >
      <DialogTitle sx={{ fontFamily: '"Plus Jakarta Sans", sans-serif', fontWeight: 600 }}>
        Edit Customer User
      </DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2.25, pt: '8px !important' }}>
        {error && <Alert severity="error">{error}</Alert>}
        {/* Legacy Replit accounts keep their username visible (read-only) —
            it still works for sign-in. Accounts created here have none. */}
        {user?.username && (
          <TextField
            label="Replit username (legacy)"
            value={user.username}
            disabled
            helperText="From the old onboarding app — still accepted at sign-in."
          />
        )}
        <TextField
          label="Full name"
          required
          value={name}
          onChange={e => setName(e.target.value)}
        />
        <TextField
          label="Email"
          type="email"
          required={!user?.username}
          value={email}
          onChange={e => setEmail(e.target.value)}
          helperText={user?.username ? 'Optional on legacy accounts.' : 'The customer signs in with this address.'}
        />
        <TextField label="Phone" value={phone} onChange={e => setPhone(e.target.value)} />
        <Autocomplete
          options={locations}
          value={location}
          onChange={(_, value) => setLocation(value)}
          getOptionLabel={l => l.name}
          renderInput={params => <TextField {...params} label="Location" />}
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <button className="ea-btn text" onClick={onClose} disabled={updateMutation.isPending}>
          Cancel
        </button>
        <button
          className="ea-btn contained"
          disabled={!canSubmit || updateMutation.isPending}
          onClick={() => {
            setError(null);
            updateMutation.mutate();
          }}
        >
          {updateMutation.isPending ? 'Saving…' : 'Save changes'}
        </button>
      </DialogActions>
    </Dialog>
  );
};

// ---- Reset password ----

interface ResetPasswordDialogProps {
  user: EdgeOnboardCustomerUser | null;
  onClose: () => void;
  onReset: () => void;
}

const ResetPasswordDialog: React.FC<ResetPasswordDialogProps> = ({ user, onClose, onReset }) => {
  const [password, setPassword] = React.useState('');
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (user) {
      setPassword('');
      setError(null);
    }
  }, [user]);

  const resetMutation = useMutation({
    mutationFn: () => {
      if (!user) throw new Error('No user');
      return edgeOnboardApi.customerUsers.update(user.id, { password });
    },
    onSuccess: onReset,
    onError: err => setError(extractApiError(err, 'Failed to reset password')),
  });

  const canSubmit = password.length >= MIN_PASSWORD_LENGTH;
  const name = user ? fullName(user) : '';

  return (
    <Dialog
      open={Boolean(user)}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ className: 'eo-scope' }}
    >
      <DialogTitle sx={{ fontFamily: '"Plus Jakarta Sans", sans-serif', fontWeight: 600 }}>
        Reset password
      </DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2.25, pt: '8px !important' }}>
        {error && <Alert severity="error">{error}</Alert>}
        <div className="eo-callout warning">
          <EoIcon name="warning" />
          <div>
            Resetting signs {name} out of all devices and requires a password change at next sign-in.
          </div>
        </div>
        <TextField
          label="New password"
          type="password"
          required
          value={password}
          onChange={e => setPassword(e.target.value)}
          helperText="At least 8 characters."
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <button className="ea-btn text" onClick={onClose} disabled={resetMutation.isPending}>
          Cancel
        </button>
        <button
          className="ea-btn contained"
          disabled={!canSubmit || resetMutation.isPending}
          onClick={() => {
            setError(null);
            resetMutation.mutate();
          }}
        >
          {resetMutation.isPending ? 'Resetting…' : 'Reset password'}
        </button>
      </DialogActions>
    </Dialog>
  );
};

// ---- Soft-delete ----

interface DeleteUserDialogProps {
  user: EdgeOnboardCustomerUser | null;
  onClose: () => void;
  onDeleted: () => void;
}

const DeleteUserDialog: React.FC<DeleteUserDialogProps> = ({ user, onClose, onDeleted }) => {
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (user) setError(null);
  }, [user]);

  const deleteMutation = useMutation({
    mutationFn: () => {
      if (!user) throw new Error('No user');
      return edgeOnboardApi.customerUsers.delete(user.id);
    },
    onSuccess: onDeleted,
    onError: err => setError(extractApiError(err, 'Failed to soft-delete customer user')),
  });

  const name = user ? fullName(user) : '';

  return (
    <Dialog
      open={Boolean(user)}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ className: 'eo-scope' }}
    >
      <DialogTitle sx={{ fontFamily: '"Plus Jakarta Sans", sans-serif', fontWeight: 600 }}>
        Soft-delete {name}?
      </DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2.25, pt: '8px !important' }}>
        {error && <Alert severity="error">{error}</Alert>}
        <p style={{ margin: 0 }}>
          Soft-deleting removes sign-in access immediately. Their onboarding history stays, and you
          can restore them later.
        </p>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <button className="ea-btn text" onClick={onClose} disabled={deleteMutation.isPending}>
          Cancel
        </button>
        <button
          className="ea-btn danger outlined"
          disabled={deleteMutation.isPending}
          onClick={() => {
            setError(null);
            deleteMutation.mutate();
          }}
        >
          {deleteMutation.isPending ? 'Soft-deleting…' : 'Soft-delete'}
        </button>
      </DialogActions>
    </Dialog>
  );
};
